import React from 'react';
import { applyTypography } from '@repo/common';

/**
 * 정책 문서 공통 레이아웃. (Figma: Public_Footer_Terms/Privacy)
 * 720px 본문, 섹션 간 48px 간격, 헤더(제목 H5 + 최종 수정일 B14) + 섹션(제목 title3 + 본문 B16).
 *
 * @param {string} title - 문서 제목
 * @param {string} lastUpdated - 최종 수정일 표기 (예: "June 11, 2026")
 * @param {Array<{chapter?: string, heading: string, body: Array<string|React.ReactNode|{type: 'table', headers: string[], rows: string[][]}>}>} sections
 *   본문 라인이 빈 문자열('')이면 빈 줄(스페이서)로 렌더링된다.
 */
const PolicyTable = ({ headers = [], rows = [] }) => {
  const minWidthClass =
    headers.length >= 4
      ? 'min-w-[900px]'
      : headers.length === 3
        ? 'min-w-[720px]'
        : 'min-w-[520px]';

  return (
    // 표는 앞 문단과 붙으면 답답해서 위아래로 여백을 준다
    <div className='my-4 w-full overflow-x-auto rounded-lg border border-grayscale-gray03'>
      <table
        className={`${applyTypography('b14')} ${minWidthClass} w-full border-collapse text-left text-text-secondary`}
      >
        <thead className='bg-grayscale-gray01 text-text-primary'>
          <tr>
            {headers.map((header) => (
              <th
                key={header}
                scope='col'
                className={`${applyTypography('b14Strong')} border-b border-r border-grayscale-gray03 px-4 py-3 align-top last:border-r-0`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  key={`${rowIndex}-${cellIndex}`}
                  /* 셀 안의 \n을 줄바꿈으로 살린다 (법정 근거처럼 여러 줄인 셀이 있다) */
                  className='whitespace-pre-line border-b border-r border-grayscale-gray03 px-4 py-3 align-top last:border-r-0'
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PolicyPage = ({
  title,
  lastUpdated,
  lastUpdatedLabel = 'Last updated',
  sections = [],
  /** 본문 아래에 붙일 영역 (버전 이동 등) */
  footer = null,
  notice = null,
  toolbar = null,
}) => {
  return (
    <div className='flex justify-center w-full bg-grayscale-gray00'>
      <div className='flex flex-col gap-[48px] items-start w-full max-w-[768px] px-[24px] pt-[64px] pb-[120px] break-words'>
        {/* Header */}
        <div className='flex flex-col gap-[12px] items-start w-full'>
          <h1 className={`${applyTypography('h5')} text-text-primary w-full`}>
            {title}
          </h1>
          <p className={`${applyTypography('b14')} text-text-secondary w-full`}>
            {lastUpdatedLabel}: {lastUpdated}
          </p>
          {toolbar}
        </div>

        {notice}

        {sections.map((section, i) => (
          <section
            key={i}
            className='flex flex-col gap-[12px] items-start w-full'
          >
            {section.chapter && (
              <h2
                className={`${applyTypography('title2')} text-text-primary w-full`}
              >
                {section.chapter}
              </h2>
            )}
            {/* 머리말(조항 앞 문단)은 제목 없이 */}
            {section.heading && (
              <h2
                className={`${applyTypography('title3')} text-text-primary w-full`}
              >
                {section.heading}
              </h2>
            )}
            <div
              className={`${applyTypography('b16')} text-text-secondary w-full`}
            >
              {section.body.map((line, j) => {
                if (
                  !React.isValidElement(line) &&
                  line?.type === 'table'
                ) {
                  return (
                    <PolicyTable
                      key={j}
                      headers={line.headers}
                      rows={line.rows}
                    />
                  );
                }

                return line === '' ? (
                  <p key={j} className='leading-[1.5]'>
                    &nbsp;
                  </p>
                ) : (
                  <p key={j} className='leading-[1.5] whitespace-pre-wrap'>
                    {line}
                  </p>
                );
              })}
            </div>
          </section>
        ))}

        {footer}
      </div>
    </div>
  );
};

export default PolicyPage;
